import { ParsedChannelData, ParsedChannelNumber, ImportError } from "../types/import";


export function parseImportData(
  data: string,
  format: 'name_first' | 'number_first',
  channelTypes: Array<{ id: number; name: string; is_default?: boolean; is_sequence?: boolean }>
): { data: ParsedChannelData[]; errors: ImportError[] } {
  const lines = data.split('\n').filter(line => line.trim());
  const errors: ImportError[] = [];
  const parsedData: ParsedChannelData[] = [];

  // Check if this is a sequential provider
  const hasSequenceType = channelTypes.some(t => t.is_sequence);

  if (hasSequenceType) {
    // For sequential providers, just parse names
    lines.forEach((line, index) => {
      const name = line.trim();
      const lineNumber = index + 1;

      if (!name) {
        errors.push({
          line: lineNumber,
          message: 'Channel name is required',
          type: 'validation',
          field: 'name'
        });
        return;
      }

      // For sequential providers, we don't need numbers as they'll be auto-generated
      parsedData.push({
        name,
        line: lineNumber,
        numbers: [], // Empty array as numbers will be assigned later
        planIds: [],
        categoryId: undefined
      });
    });
  } else {
    // Non-sequential providers logic
    const expectedParts = channelTypes.length + 1;

    lines.forEach((line, index) => {
      const lineNumber = index + 1;
      const parts = line.split('|').map(part => part.trim());
      const isNameFirst = format === 'name_first';

      // Validate parts length
      if (parts.length > expectedParts) {
        errors.push({
          line: lineNumber,
          message: `Too many values provided. Expected ${expectedParts} values (name + ${channelTypes.length} numbers)`,
          type: 'validation',
          field: 'format'
        });
        return;
      }

      // Get name based on format
      const name = isNameFirst ? parts[0] : parts[parts.length - 1];
      if (!name) {
        errors.push({
          line: lineNumber,
          message: 'Channel name is required',
          type: 'validation',
          field: 'name'
        });
        return;
      }

      // Parse numbers
      const numberParts = isNameFirst ? parts.slice(1) : parts.slice(0, -1);
      
      // Check if at least one non-asterisk number exists
      const hasValidNumber = numberParts.some(num => num !== '*' && num.trim() !== '');
      if (!hasValidNumber) {
        errors.push({
          line: lineNumber,
          message: 'At least one valid number is required for each channel',
          type: 'validation',
          field: 'number'
        });
        return;
      }

      const numbers: ParsedChannelNumber[] = channelTypes.map((type, i) => {
        const numberValue = numberParts[i]?.trim();
        
        // Handle asterisk and empty values
        if (!numberValue || numberValue === '*') {
          return {
            typeId: type.id,
            number: '*',
            isSequence: false,
            is_default: type.is_default || false
          };
        }

        // Validate number format
        if (!/^[0-9]+([/\-\.][0-9]+)*$/.test(numberValue)) {
          errors.push({
            line: lineNumber,
            message: `Invalid number format: ${numberValue}`,
            type: 'validation',
            field: 'number'
          });
        }

        return {
          typeId: type.id,
          number: numberValue,
          isSequence: false,
          is_default: type.is_default || false
        };
      });

      parsedData.push({
        name,
        line: lineNumber,
        numbers,
        planIds: [],
        categoryId: undefined
      });
    });
  }

  return { data: parsedData, errors };
}

// Helper function to validate number format
export function validateChannelNumber(number: string): boolean {
  if (!number || number === '*') return true;
  // Updated to exclude only control characters, matching database validation
  return /^[^\x00-\x1F\x7F]+$/.test(number);
}